/**
 * Formatteer een lange/rommelige car_model-string tot een net leesbaar label.
 *
 * Gedrag: 100% display-only — de originele string blijft de filter-key.
 * Filtering blijft dus intact (backend gebruikt substring regex op car_model).
 *
 * Returns { display, details }
 *   display: korte hoofdtitel (max ~40 chars)
 *   details: aanvullende specs (motor, BHP, bouwjaar, silencer-restanten weggefilterd)
 */

// Merken die vaak in slash-lijsten voorkomen — als we in de context van een van
// deze merken staan, willen we alleen het relevante deel tonen.
const KNOWN_MAKES = new Set([
  "audi", "seat", "skoda", "volkswagen", "vw", "bmw", "mini", "mercedes",
  "mercedes-benz", "ford", "opel", "renault", "peugeot", "citroen", "toyota",
  "lexus", "honda", "acura", "nissan", "infiniti", "subaru", "mitsubishi",
  "mazda", "hyundai", "kia", "chevrolet", "dodge", "ram", "chrysler", "gmc",
  "cadillac", "buick", "porsche", "volvo", "saab", "fiat", "alfa", "abarth",
  "jaguar", "land rover", "range rover", "mini",
]);

// Restanten van exhaust/silencer catalogus die soms in car_model terechtkomen
// (deze zijn ECHT ruis en niet betekenisvol voor de klant)
const NOISE_PATTERNS = [
  /\bsilencer\s+\([^)]*$/gi,        // "Silencer (954112 1600"
  /\bsilencer\s+\(\d+[^)]*\)/gi,    // "Silencer (955113 150)"
  /\bfaclift\b/gi,                   // typo "facelift" (blijft als "facelift" behouden)
  /\s+\d+mm\b/gi,                    // "170mm"
];

// Details die we willen extraheren voor de subtitle
const DETAIL_PATTERNS = [
  { re: /\((\d{4})[\s-]*(\d{4}|\-)?\s*\)/g, label: "yr" }, // (2013-2020) / (2020-)
  { re: /\b(\d+(?:\.\d+)?)\s*(?:L|l)?\s*(TDI|TFSI|TSI|FSI|VTEC|VVT[- ]?[Ii]?|MPI|GDI|CDI|CRDI|HDI|DCI|TDCi|BiTDI|Twin Turbo|Turbo Diesel|Turbo|Supercharged|Hybrid|EV|BEV)\b/gi, label: "eng" },
  { re: /\b(\d{2,4})\s*BHP\b/gi, label: "bhp" },
  { re: /\b(\d{2,4})\s*(HP|PK|KW)\b/gi, label: "power" },
];

/**
 * Split een slash-lijst maar bewaar model-codes tussen haakjes intact.
 * "AUDI/SEAT/SKODA 1.2/1.4TSI" → ["AUDI 1.2TSI", "SEAT 1.2TSI", ...] is te lastig.
 * We doen het simpel: als een deel voor de spatie een auto-merk is, strippen we het merk-deel.
 */
function stripLeadingMakes(model, contextMake) {
  const trimmed = (model || "").trim().replace(/^[/,\s]+/, "");  // strip leading slashes, commas
  if (!trimmed) return trimmed;
  const contextLc = (contextMake || "").toLowerCase();

  // Patroon: "MAKE1/MAKE2/MAKE3 RestOfModel" → als contextMake voorkomt, houd alleen "RestOfModel"
  const firstSpace = trimmed.indexOf(" ");
  if (firstSpace > 0) {
    const head = trimmed.slice(0, firstSpace);
    const tail = trimmed.slice(firstSpace + 1);
    if (head.includes("/")) {
      const parts = head.split("/").map((p) => p.trim()).filter(Boolean);
      const allMakes = parts.length > 0 && parts.every((p) => KNOWN_MAKES.has(p.toLowerCase()));
      const containsContext = contextLc && parts.some((p) => p.toLowerCase() === contextLc);
      if (allMakes && containsContext) {
        return tail;
      }
      if (allMakes && !contextLc) {
        // Geen context: pak het eerste merk als prefix
        return `${parts[0]} ${tail}`;
      }
    }
  }
  return trimmed;
}

/**
 * Hoofdfunctie: formatteer 1 modelnaam.
 * @param {string} model - originele car_model waarde
 * @param {string} contextMake - het huidige geselecteerde car_make (optioneel)
 * @returns {{ display: string, details: string }}
 */
export function formatCarModel(model, contextMake = "") {
  if (!model) return { display: "", details: "" };
  let s = String(model).trim();

  // Stap 1: strip merk-prefixen bij slash-lijsten
  s = stripLeadingMakes(s, contextMake);

  // Stap 2: extract details (jaren, motorspecs, vermogen) voordat we ze verwijderen
  const details = [];

  // Bouwjaar
  const yearMatch = s.match(/\((\d{4})\s*[-–]\s*(\d{4}|\s*)\)/);
  if (yearMatch) {
    const from = yearMatch[1];
    const to = (yearMatch[2] || "").trim();
    details.push(to ? `${from}-${to}` : `${from}-nu`);
    s = s.replace(yearMatch[0], "").trim();
  } else {
    const singleYear = s.match(/\((\d{4})[-–]?\)/);
    if (singleYear) {
      details.push(`${singleYear[1]}-nu`);
      s = s.replace(singleYear[0], "").trim();
    }
  }

  // BHP / vermogen
  const bhpMatch = s.match(/\b(\d{2,4})\s*BHP\b/i);
  if (bhpMatch) {
    details.push(`${bhpMatch[1]} pk`);
    s = s.replace(bhpMatch[0], "").trim();
  }
  const kwMatch = s.match(/\b(\d{2,4})\s*KW\b/i);
  if (kwMatch && !bhpMatch) {
    details.push(`${kwMatch[1]} kW`);
  }
  s = s.replace(/\b\d{2,4}\s*(KW|HP|PK)\b/gi, "").trim();

  // Cilinderinhoud + brandstoftype (blijft in DISPLAY, niet details)
  // Alleen strip als het aan het EIND staat en overbodig is
  // Skip — we willen "2.0 TDI" in de titel houden voor herkenbaarheid

  // Stap 3: strip noise
  for (const p of NOISE_PATTERNS) {
    s = s.replace(p, "").trim();
  }

  // Stap 4: whitespace opschonen + haakjes opruimen
  s = s
    .replace(/\s{2,}/g, " ")
    .replace(/\(\s*\)/g, "")
    .replace(/\s+\)/g, ")")
    .replace(/\(\s+/g, "(")
    .replace(/,\s*,/g, ",")
    .replace(/\s*[·|]\s*$/g, "")
    .replace(/^\s*[·|,]\s*/g, "")
    .replace(/\s+$/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();

  // Stap 5: als display > 40 chars, truncate op laatste woord/haakje
  let display = s;
  if (display.length > 40) {
    // Probeer te knippen op laatste spatie voor 40
    const cut = display.lastIndexOf(" ", 40);
    display = (cut > 15 ? display.slice(0, cut) : display.slice(0, 40)) + "…";
  }

  // Stap 6: leeg? val terug op origineel
  if (!display) display = String(model).slice(0, 40);

  return {
    display,
    details: details.join(" · "),
  };
}

/**
 * Compacte enkele string (voor plekken waar je maar één regel hebt zoals <option>).
 * Combineert display + details maar respecteert een max lengte.
 */
export function formatCarModelInline(model, contextMake = "", maxLen = 60) {
  const { display, details } = formatCarModel(model, contextMake);
  if (!details) return display;
  const combined = `${display} · ${details}`;
  if (combined.length <= maxLen) return combined;
  return display;
}
